/**
 * Talent Review Service - API calls for annual talent review process
 * 
 * Features:
 * - Talent profiles (3-year performance history)
 * - 9-Box Grid positioning
 * - Succession planning
 * - Key person designation
 * - HIPO (High Potential) tracking
 * - Bench strength analysis
 * 
 * Reference: Manager-Journey.md #649-660
 */

import { API_CONFIG } from '@/config/api'
import { authenticatedFetch } from '../authenticatedFetch'

// ============================================================================
// TYPES
// ============================================================================

/** Error carrying the server's own status and refusal text. */
export interface TalentReviewApiError extends Error {
  status: number
  detail: string
}

export type PerformanceLevel = 'high' | 'medium' | 'low'
export type PotentialLevel = 'high' | 'medium' | 'low'
export type SuccessorReadiness = 'ready-now' | 'ready-1-year' | 'ready-2-plus'
export type RetentionRisk = 'low' | 'medium' | 'high' | 'critical'
export type HIPOCategory = 'star' | 'high-professional' | 'high-potential'

// ============================================================================
// INTERFACES - TALENT PROFILES
// ============================================================================

export interface PerformanceHistoryItem {
  year: number
  review_type: 'mid-year' | 'year-end'
  rating: string
  kpa_ratings?: Record<string, string>
}

export interface QualificationItem {
  type: 'degree' | 'certification' | 'diploma'
  name: string
  institution?: string
  year?: number
}

export interface CareerHistoryItem {
  role: string
  organization: string
  start_date: string
  end_date?: string
  is_current: boolean
}

export interface DevelopmentSummary {
  idp_status?: string
  completed_interventions: string[]
  in_progress_interventions: string[]
  skills_developed: string[]
}

export interface MobilityPreferences {
  function: boolean
  role: boolean
  geographic: boolean
  notes?: string
}

export interface TalentProfile {
  id: string
  user_id: string
  performance_history: PerformanceHistoryItem[]
  performance_trend?: string
  average_rating?: string
  qualifications: QualificationItem[]
  career_history: Record<string, CareerHistoryItem[]>
  development_summary?: DevelopmentSummary
  existing_potential_category?: PotentialLevel
  career_aspirations?: string
  short_term_goals?: string
  long_term_goals?: string
  mobility_preferences?: MobilityPreferences
  is_complete: boolean
  completeness_percentage: number
  last_generated_at?: string
  last_updated_at: string
  user_name?: string
  user_role?: string
  user_department?: string
  tenure_years?: number
}

// ============================================================================
// INTERFACES - 9-BOX GRID
// ============================================================================

export interface NineBoxPosition {
  id: string
  user_id: string
  profile_id?: string
  review_year: number
  performance_level: PerformanceLevel
  potential_level: PotentialLevel
  positioned_by: string
  positioning_rationale?: string
  evidence_references?: string[]
  confirmed_in_session: boolean
  confirmed_by?: string
  confirmed_at?: string
  position_changed: boolean
  created_at: string
  updated_at: string
  user_name?: string
  user_role?: string
  user_photo?: string
  tenure_years?: number
}

export interface NineBoxPositionCreate {
  user_id: string
  profile_id?: string
  review_year: number
  performance_level: PerformanceLevel
  potential_level: PotentialLevel
  positioned_by: string
  positioning_rationale?: string
  evidence_references?: string[]
}

export interface NineBoxGrid {
  review_year: number
  positions: Record<string, NineBoxPosition[]>
  total_count: number
  hipo_count: number
}

// ============================================================================
// INTERFACES - SUCCESSION PLANNING
// ============================================================================

export interface SuccessionCandidate {
  id: string
  succession_plan_id: string
  candidate_id: string
  readiness: SuccessorReadiness
  readiness_rationale?: string
  development_needs?: string
  development_plan?: Array<Record<string, unknown>>
  priority_rank?: number
  assessed_by?: string
  assessed_at?: string
  created_at: string
  candidate_name?: string
  candidate_role?: string
  candidate_department?: string
}

export interface SuccessionCandidateCreate {
  candidate_id: string
  readiness: SuccessorReadiness
  readiness_rationale?: string
  development_needs?: string
  development_plan?: Array<Record<string, unknown>>
  priority_rank?: number
  assessed_by?: string
}

export interface SuccessionPlan {
  id: string
  critical_role_id?: string
  critical_role_name: string
  role_level?: string
  current_incumbent_id?: string
  organizational_unit_id?: string
  organizational_unit_name?: string
  review_year: number
  succession_risk?: string
  vacancy_risk?: string
  ready_now_count: number
  ready_1_year_count: number
  ready_2_plus_count: number
  notes?: string
  action_items?: Array<Record<string, unknown>>
  created_by: string
  created_at: string
  updated_at: string
  candidates: SuccessionCandidate[]
  incumbent_name?: string
}

export interface SuccessionPlanCreate {
  critical_role_name: string
  critical_role_id?: string
  role_level?: string
  current_incumbent_id?: string
  organizational_unit_id?: string
  organizational_unit_name?: string
  review_year: number
  created_by: string
  notes?: string
}

export interface SuccessionRole {
  id: string
  name: string
  department?: string
  functional_area?: string
  position_count?: number
  critical?: boolean
}

// ============================================================================
// INTERFACES - KEY PERSONS
// ============================================================================

export interface KeyPerson {
  id: string
  user_id: string
  review_year: number
  designation_reason: string
  critical_knowledge_areas?: string[]
  organizational_impact?: string
  retention_risk?: RetentionRisk
  retention_risk_factors?: string[]
  retention_strategy?: string
  knowledge_transfer_plan?: string
  knowledge_transfer_progress?: number
  designated_by: string
  designated_at: string
  is_active: boolean
  created_at: string
  updated_at: string
  user_name?: string
  user_role?: string
  user_department?: string
}

export interface KeyPersonCreate {
  user_id: string
  review_year: number
  designated_by: string
  designation_reason: string
  critical_knowledge_areas?: string[]
  organizational_impact?: string
  retention_risk?: RetentionRisk
  retention_risk_factors?: string[]
  retention_strategy?: string
}

// ============================================================================
// INTERFACES - HIPO
// ============================================================================

export interface HIPODesignation {
  id: string
  user_id: string
  review_year: number
  source_9box_position_id: string
  hipo_category: string
  development_plan?: string
  development_priorities?: string[]
  fast_track_programs?: string[]
  enrolled_programs?: string[]
  retention_priority: boolean
  engagement_score?: number
  idp_integration: boolean
  idp_id?: string
  created_at: string
  updated_at: string
  user_name?: string
  user_role?: string
  user_department?: string
  performance_level?: PerformanceLevel
  potential_level?: PotentialLevel
}

// ============================================================================
// INTERFACES - BENCH STRENGTH
// ============================================================================

export interface BenchStrengthAnalysis {
  id: string
  review_year: number
  analysis_level: string
  scope_name: string
  scope_id?: string
  organizational_unit_id?: string
  organizational_unit_name?: string
  total_positions: number
  positions_with_succession: number
  coverage_percentage: number
  ready_now_count: number
  ready_1_year_count: number
  ready_2_plus_count: number
  no_successor_count: number
  overall_risk?: string
  critical_gaps?: Array<Record<string, unknown>>
  recommendations?: Array<Record<string, unknown>>
  previous_coverage?: number
  coverage_trend?: string
  analyzed_at: string
}

// ============================================================================
// INTERFACES - TALENT REVIEW SESSIONS (Enhanced with phase workflow)
// ============================================================================

export type TalentReviewSessionStatus = 
  | 'scheduled'
  | 'pre-review'
  | 'report-ready'
  | 'in-session'
  | 'post-review'
  | 'completed'
  | 'cancelled'

export type TalentReviewParticipantRole = 
  | 'facilitator'
  | 'manager'
  | 'one-up-manager'
  | 'hrbp'
  | 'observer'

export type LeadershipPipelineLevel =
  | 'manage-self'
  | 'manage-others'
  | 'manage-managers'
  | 'functional-manager'
  | 'business-manager'
  | 'group-manager'
  | 'enterprise-manager'

export type DrotterPotentialLevel = 'turn' | 'growth' | 'mastery'

export type PreSubmissionStatus = 'draft' | 'submitted' | 'reviewed' | 'flagged'


// ---------------------------------------------------------------------------
// Tier 1 placement checklist (People Leader preparation)
// ---------------------------------------------------------------------------
/** The 70/20/10 development plan, one line each. */
export interface DevelopmentPlan {
  experience?: string | null
  exposure?: string | null
  education?: string | null
}

export interface PlacementSubmissionState {
  id: string
  status: PreSubmissionStatus
  performance: PerformanceLevel | null
  potential: PotentialLevel | null
  rationale: string | null
  nominated_as_key_person: boolean
  key_person_rationale: string | null
  not_placeable_reason: string | null
  submitted_at: string | null
  // Tier 2 — blocks 1 to 3
  next_role?: string | null
  readiness?: SuccessorReadiness | null
  strengths?: string | null
  growth_areas?: string | null
  development_plan?: DevelopmentPlan | null
  // Tier 2 — key person
  retention_risk?: RetentionRisk | null
  critical_knowledge_areas?: string[]
  retention_mitigations?: string | null
  /** What the mid-year rating suggested when the leader decided, and why they went elsewhere. */
  impact_default?: PerformanceLevel | null
  impact_reason?: string | null
  /** The room amended the growth details after the session started. */
  growth_amended_by?: string | null
  growth_amended_by_name?: string | null
  growth_amended_at?: string | null
  /** An edit made since the list was handed in, kept beside it until the list is
   *  handed in again; what was submitted stands until then (Victor, 1 Oct 2026). */
  pending?: (Omit<PlacementInput, 'potential' | 'performance'> & {
    potential?: PotentialLevel | null
    performance?: PerformanceLevel | null
  }) | null
  /** When that edit was kept (UTC, without a zone). */
  pending_saved_at?: string | null
}

export interface PlacementRosterItem {
  employee_id: string
  name: string
  job_title: string | null
  department: string | null
  campus: string | null
  /** Mid-year rating as stored: exceptional | full | not_yet_full | weak | tstr */
  midyear_rating: string | null
  /** Every formal rating on record, oldest first — the impact history. */
  rating_history: TalentRatingRecord[]
  /** The year's mid-year KPA ratings. */
  midyear_kpas: TalentKpaRating[]
  /** The person's Personalised Growth plan for the year; null when they have none yet. */
  idp: TalentGridIdp | null
  /** What their growth plan still needs; empty when complete. */
  growth_plan_gaps: string[]
  /** What the mid-year rating suggests as the impact category — the default; null when there is no rating. */
  prefilled_performance: PerformanceLevel | null
  not_placeable_reason: string | null
  submission: PlacementSubmissionState | null
}

export interface PlacementRoster {
  session_id: string
  manager_id: string
  review_year: number
  session_status: TalentReviewSessionStatus
  locked: boolean
  /** The date the chair set for placements to be in, if any. */
  pre_review_deadline: string | null
  items: PlacementRosterItem[]
  summary: {
    total: number
    placeable: number
    not_placeable: number
    submitted: number
    plans_incomplete: number
    /** Placements edited since the list was handed in, not yet handed in again. */
    changes_pending?: number
  }
}

export interface PlacementInput {
  employee_id: string
  potential?: PotentialLevel
  rationale?: string
  nominated_as_key_person?: boolean
  key_person_rationale?: string
  // Tier 2 — blocks 1 to 3 (readiness is required to submit)
  next_role?: string
  readiness?: SuccessorReadiness
  strengths?: string
  growth_areas?: string
  development_plan?: DevelopmentPlan
  // Tier 2 — key person (retention risk is required to submit)
  retention_risk?: RetentionRisk
  critical_knowledge_areas?: string[]
  retention_mitigations?: string
  /** The impact category, when the leader judges it away from the mid-year default (reason required). */
  performance?: PerformanceLevel
  impact_reason?: string
}

/** What the room changes on a high-potential’s growth details once the
 *  session has started. Readiness is required once a next position is named. */
export interface GrowthDetailsAmendment {
  next_role?: string | null
  readiness?: SuccessorReadiness | null
  growth_areas?: string | null
  development_plan?: DevelopmentPlan | null
}

export interface PlacementBatchInput {
  manager_id?: string
  manager_name?: string
  placements: PlacementInput[]
  submit?: boolean
}

/** The room's running order, as the HR process owner set it. */
export type AgendaStepId = 'reservations' | 'gaps' | 'hipos' | 'too_soon' | 'key_persons' | 'successors'

// ---------------------------------------------------------------------------
// The HIPO worklist for the Good Governance People Forum
// ---------------------------------------------------------------------------
export interface HipoWorklistItem {
  employee_id: string
  name: string | null
  job_title: string | null
  department: string | null
  campus: string | null
  leader_name: string | null
  block: number | null
  hipo_category: string | null
  next_role: string | null
  readiness: SuccessorReadiness | null
  /** The plan as published: strengths, growth areas and the 70/20/10 lines, one per line. */
  development_plan: string | null
  midyear_rating: string | null
  idp: TalentGridIdp | null
  /** The published items went into the person's Personalised Growth plan. */
  idp_linked: boolean
  /** Items on the plan still waiting on the employee or the leader. */
  proposed_items: number
  forum_reviewed_at: string | null
  forum_reviewed_by: string | null
  forum_review_note: string | null
}

export interface HipoWorklist {
  review_year: number
  items: HipoWorklistItem[]
}

// ---------------------------------------------------------------------------
// Successors for the leader's own role (the HR process owner's process notes, 7 Sep 2026)
// ---------------------------------------------------------------------------
export type SuccessorStatus = 'proposed' | 'confirmed' | 'rejected'

export interface SuccessorNomination {
  id: string
  session_id: string
  manager_id: string
  manager_name: string | null
  role_title: string | null
  position_id: string | null
  candidate_id: string
  candidate_name: string | null
  candidate_title: string | null
  readiness: SuccessorReadiness
  rationale: string | null
  status: SuccessorStatus
  decided_by: string | null
  decided_at: string | null
  decision_note: string | null
}

export interface SuccessorNominationList {
  session_id: string
  manager_id: string | null
  /** The leader's role as the reporting line names it, when one leader's list was asked for. */
  role_title: string | null
  locked: boolean
  nominations: SuccessorNomination[]
}

export interface SuccessorNominationInput {
  candidate_id: string
  candidate_name?: string
  candidate_title?: string
  readiness: SuccessorReadiness
  rationale?: string
}

export interface SuccessorNominationBatchInput {
  manager_id?: string
  role_title?: string
  nominations: SuccessorNominationInput[]
}

/** A successor the room adds for a leader's role while the session runs (Victor, 8 Sep 2026). */
export interface RoomSuccessorInput {
  manager_id: string
  candidate_id: string
  candidate_name?: string
  candidate_title?: string
  readiness: SuccessorReadiness
  rationale?: string
}

/** Someone in the platform directory, as the pickers list them. */
export interface DirectoryPerson {
  id: string
  name: string
  title: string | null
  department: string | null
  campus: string | null
  email: string | null
}

// ---------------------------------------------------------------------------
// Talent grid (the talent map) — one call, every person in scope enriched
// ---------------------------------------------------------------------------
export type TalentGridMode = 'session' | 'published'
export type TalentGridPersonStatus = 'pending' | 'draft' | 'submitted' | 'reviewed' | 'published'

/** A seat held before the current one. */
export interface TalentGridRole {
  title: string | null
  from: string | null
  to: string | null
}

/** One of the year's mid-year KPA ratings. */
export interface TalentKpaRating {
  name: string
  rating: string
}

/** One formal rating on record: the impact history. */
export interface TalentRatingRecord {
  review_year: number
  /** midyear | yearend */
  review_type: string
  /** exceptional | full | not_yet_full | weak | tstr, or F&O's wording when it does not fit the scale */
  rating: string
  calibrated: boolean
  /** platform, or fo for a review F&O held before the platform */
  source?: 'platform' | 'fo'
  /** F&O's rating description as written, for a legacy review */
  label?: string | null
}

/** One item on a Personalised Growth plan, as the checklist lists it. */
export interface TalentGridPlanItem {
  id: string
  title: string
  /** APPROVED | IN_PROGRESS | COMPLETED | EMPLOYEE_PROPOSED | MANAGER_ADDED | AI_GENERATED */
  status: string | null
  /** An area type or an intervention type. */
  kind: string | null
  priority: number | null
  /** Interventions: the stored percentage. Nothing in my Personalised Growth
   *  maintains it, so the review does not show it (Victor, 7 Sep 2026). */
  progress: number | null
}

/** The person's Personalised Growth plan for the year, as the Annual I&G
 *  Review reads it — with whether it is complete and what it still needs. */
// ---------------------------------------------------------------------------
// The campus General Manager's input space
//
// A campus GM and the HR process owner (8 Sep 2026): the GM sees the campus daily and answers for
// succession on it, but most of the campus reports up a function. So the GM
// gets the whole campus — ungated, outside the sessions — to record what they
// see, nominate key persons and successors, and flag a cross-functional fit.
// ---------------------------------------------------------------------------

/** One author's input on one person. */
export interface CampusInput {
  id: string
  review_year: number
  employee_id: string
  author_id: string
  author_name: string | null
  author_job_title: string | null
  insight: string | null
  key_person: boolean
  key_person_reason: string | null
  /** Where they need to grow — the other half of the strengths (HR process owner, 9 Sep 2026). */
  growth_areas: string | null
  /** The roles the campus sees them growing into, best readiness first. */
  succeeding_roles: CampusSucceedingRole[]
  cross_functional_area: string | null
  cross_functional_note: string | null
  updated_at: string | null
}

/** What the GM writes. Everything optional; an empty input clears the record. */
export interface CampusInputWrite {
  review_year: number
  insight?: string | null
  key_person?: boolean
  key_person_reason?: string | null
  growth_areas?: string | null
  succeeding_roles?: CampusSucceedingRole[]
  cross_functional_area?: string | null
  cross_functional_note?: string | null
}

export interface CampusSucceedingRole {
  title: string
  readiness: SuccessorReadiness | null
}

export interface CampusOption {
  name: string
  /** The code with the location most of its people sit at: "ON - mostly Support".
      The financial dimension has no name of its own in the platform. */
  label: string
  people: number
  /** People this caller has already written about. */
  inputs: number
  is_own_reach: boolean
}

export interface CampusListResponse {
  review_year: number
  campuses: CampusOption[]
  can_view_all: boolean
}

/** One person at the campus. Deliberately no 9-box: the block, the impact
 *  category and the potential stay with the People Leader and the room. */
export interface CampusPerson {
  employee_id: string
  name: string
  initials: string
  job_title: string | null
  department: string | null
  function: string | null
  team: string | null
  campus: string | null
  position_type: string | null
  employment_category: string | null
  leader_id: string | null
  leader_name: string | null
  one_up_name: string | null
  original_hire_date: string | null
  tenure_years: number | null
  role_tenure_years: number | null
  is_hipo: boolean
  key_person: boolean
  inputs: CampusInput[]
  my_input: CampusInput | null
}

export interface CampusViewResponse {
  review_year: number
  campus: string
  people: CampusPerson[]
  facets: {
    departments: string[]
    functions: string[]
    teams: string[]
    position_types: string[]
    leaders: { id: string; name: string }[]
  }
  summary: {
    total: number
    with_input: number
    key_persons_nominated: number
    successors_nominated: number
    cross_functional: number
    hipos_published: number
  }
}

/** A next possible position on the plan, with the readiness for it. */
export interface TalentGridNextRole {
  title: string
  readiness: SuccessorReadiness | null
}

export interface TalentGridIdp {
  plan_id: string | null
  focus: string | null
  /** Up to three development area titles, in priority order (hover card). */
  areas: string[]
  /** Up to two intervention titles (hover card). */
  interventions: string[]
  /** IMPROVE_CURRENT | ENHANCE_IMPACT | FUTURE_ROLE */
  focus_type: string | null
  aspiration_type: string | null
  /** The next role the person aims for, when their aspiration is a next role. */
  target_role: string | null
  future_roles: string[]
  /** yes | maybe | no */
  mobility: string | null
  preferred_campuses: string[]
  /** The People Leader's view (HR process owner, 8 Sep 2026): their growth focus for the person, why, and the next positions with a readiness each. */
  review_focus: ReviewFocus | string | null
  review_focus_label: string | null
  review_focus_reason: string | null
  /** An employee id, or 'talent_review' when the room's action set it. */
  review_focus_set_by: string | null
  review_focus_set_by_name: string | null
  review_focus_set_at: string | null
  next_roles: TalentGridNextRole[]
  strengths: string[]
  areas_detail: TalentGridPlanItem[]
  interventions_detail: TalentGridPlanItem[]
  /** Items the team member proposed that await the leader's review. */
  pending_review: number
  approved: boolean
  finalized: boolean
  complete: boolean
  /** What the plan still needs, in plain words; empty when complete. */
  gaps: string[]
}

export interface TalentGridPerson {
  employee_id: string
  name: string
  initials: string
  job_title: string | null
  department: string | null
  function: string | null
  team: string | null
  campus: string | null
  position_type: string | null
  employment_category: string | null
  grade: string | null
  leader_id: string | null
  leader_name: string | null
  one_up_id: string | null
  one_up_name: string | null
  original_hire_date: string | null
  tenure_years: number | null
  /** Mid-year rating as stored: exceptional | full | not_yet_full | weak | tstr */
  midyear_rating: string | null
  performance: PerformanceLevel | null
  potential: PotentialLevel | null
  /** Playbook block 1–9; null when not placed. */
  block: number | null
  not_placeable_reason: string | null
  status: TalentGridPersonStatus
  is_hipo: boolean
  hipo_category: string | null
  key_person: boolean
  key_person_reason: string | null
  /** A high-potential’s growth details as the room reads them live: the leader’s
   *  proposals, or the room’s amendment of them, with who amended and when. */
  growth_areas?: string | null
  development_plan?: DevelopmentPlan | null
  growth_amended_by_name?: string | null
  growth_amended_at?: string | null
  rationale: string | null
  position_changed: boolean
  changed_from: { performance: PerformanceLevel | null; potential: PotentialLevel | null } | null
  /** Tier 2, for a high-potential: the next possible position and readiness for it. */
  next_role: string | null
  readiness: SuccessorReadiness | null
  /** Tier 2, for a key person. */
  retention_risk: RetentionRisk | null
  /** Every formal rating on record, oldest first — the impact history. */
  rating_history: TalentRatingRecord[]
  midyear_kpas: TalentKpaRating[]
  /** Annual I&G Review context (HR process owner, 7 Sep 2026): tenure, contract and nationality. */
  nationality: string | null
  highest_qualification: string | null
  highest_nqf: number | null
  current_role_since: string | null
  role_tenure_years: number | null
  previous_roles: TalentGridRole[]
  /** The impact category as judged: what the mid-year suggested, and why the leader went elsewhere. */
  performance_default: PerformanceLevel | null
  impact_reason: string | null
  idp: TalentGridIdp | null
  /** What their growth plan still needs; empty when complete. */
  growth_plan_gaps: string[]
  /** What the campus General Manager said about them, given outside any session. */
  campus_inputs?: CampusInput[]
}

export interface TalentGridSessionSummary {
  id: string
  session_name: string | null
  status: TalentReviewSessionStatus
  scheduled_date: string | null
  facilitator_id: string | null
  facilitator_name: string | null
  /** The viewer is placed in this session themselves (a sales lead): the session's People
   *  Leaders are left off their grid, and the hub leaves them off its lists too. */
  seats_hidden?: boolean
}

export interface TalentGridFacets {
  functions: string[]
  departments: string[]
  campuses: string[]
  position_types: string[]
  leaders: { id: string; name: string }[]
  one_ups: { id: string; name: string }[]
}

export interface TalentGridSummary {
  total: number
  placed: number
  not_placeable: number
  pending: number
  by_performance: { low: number; medium: number; high: number }
  hipo: number
  key_persons: number
}

export interface TalentGridResponse {
  review_year: number
  mode: TalentGridMode
  session: TalentGridSessionSummary | null
  people: TalentGridPerson[]
  /** Computed over the whole scoped set, before filters. */
  facets: TalentGridFacets
  /** Computed over the filtered set. */
  summary: TalentGridSummary
}

export interface TalentGridParams {
  review_year: number
  session_id?: string
  manager_id?: string
  function?: string
  department?: string
  campus?: string
  position_type?: string
  leader_id?: string
  one_up_id?: string
}

export type DecisionType = 
  | 'position-change'
  | 'hipo-confirmed'
  | 'key-person'
  | 'succession'
  | 'development'
  | 'retention'

/** What an action the room sends to a plan becomes there: a growth
 *  opportunity, or a growth enablement of one of the four kinds. */
export type GrowthPlanItemKind = 'opportunity' | 'work_assignment' | 'coaching' | 'training' | 'education' | 'focus'
/** The growth focus the room proposes with a 'focus' action (HR process owner, 8 Sep 2026). */
export type ReviewFocus = 'IMPROVE_CURRENT' | 'ENHANCE_IMPACT' | 'FUTURE_ROLE'

/** An action or note the room recorded, as the completion summary lists it. */
export interface SessionAction {
  id: string
  employee_id: string
  employee_name?: string | null
  /** development | retention */
  kind: string
  text?: string | null
  owner_id?: string | null
  owner_name?: string | null
  due_date?: string | null
  by?: string | null
  by_name?: string | null
  at?: string | null
  to_growth_plan: boolean
  growth_plan_item?: GrowthPlanItemKind | null
  /** A 'focus' action: the growth focus proposed, and a next position named with it. */
  growth_focus?: ReviewFocus | string | null
  next_role?: string | null
  next_readiness?: SuccessorReadiness | null
}

/** Someone every Annual I&G Review invites by default (HR's standing invitees). */
export interface DefaultObserver {
  employee_id: string
  display_name: string
  job_title?: string | null
}

// Report data structure populated during report generation
export interface TalentReviewReportData {
  total_employees: number
  submissions_received: number
  grid_distribution: Record<string, number>
  hipos_proposed: Array<{
    employee_id: string
    employee_name: string
    proposed_performance: string
    proposed_potential: string
    rationale?: string | null
    impact_default?: string | null
    impact_reason?: string | null
    next_role?: string | null
    readiness?: SuccessorReadiness | null
    strengths?: string | null
    growth_areas?: string | null
    development_plan?: DevelopmentPlan | null
  }>
  key_persons_proposed: Array<{
    employee_id: string
    employee_name: string
    rationale?: string
    critical_knowledge_areas?: string[]
    retention_risk?: string
    retention_mitigations?: string | null
  }>
  flagged_items: Array<{
    employee_id: string
    employee_name: string
    reason?: string
  }>
  generated_at: string
  /** The room's agenda: step id → when it was ticked. */
  agenda_progress?: Record<string, string>
  /** Each reopening of the session, oldest first. */
  reopened?: { at: string; by: string | null; reason: string; completed_at: string | null }[]
  /** The actions and notes the room recorded, listed at completion. */
  actions?: SessionAction[]
}

export interface TalentReviewSession {
  id: string
  review_year: number
  session_name?: string
  scheduled_date: string
  /** How long the room is held; 60 for sessions booked before chairs could choose. */
  duration_minutes?: number
  status: TalentReviewSessionStatus
  facilitator_id?: string
  facilitator_name?: string
  organizational_unit_id?: string
  organizational_unit_name?: string
  agenda?: string[]
  pre_review_deadline?: string
  submissions_required: number
  submissions_completed: number
  report_generated_at?: string
  /** The seated People Leaders' names, for the session switcher. */
  people_leaders?: string[]
  /** How the person asking sits in this session: 'chair', or their seat ('manager' when they
   *  present their own team, 'hrbp', 'observer'); null when they are not in it. */
  viewer_seat?: string | null
  report_data?: TalentReviewReportData
  profiles_reviewed: number
  positions_adjusted: number
  hipos_identified: number
  hipos_confirmed: number
  key_persons_designated: number
  succession_plans_updated: number
  notes?: string
  summary?: string
  pre_review_started_at?: string
  started_at?: string
  post_review_started_at?: string
  completed_at?: string
  created_at: string
  updated_at: string
  participant_count: number
}

export interface TalentReviewSessionCreate {
  review_year: number
  session_name?: string
  scheduled_date: string
  /** How long the room is held. The service books an hour when this is left out. */
  duration_minutes?: number
  facilitator_id?: string
  facilitator_name?: string
  organizational_unit_id?: string
  organizational_unit_name?: string
  pre_review_deadline?: string
  agenda?: string[]
  /** The People Leaders seated in this session. Only ids — everything else
   *  about a seat is derived server-side. */
  participants?: { user_id: string }[]
  /** HRBPs the chair invites: a seat of their own and the calendar invite, never submitters. Employee ids. */
  observers?: string[]
  /** Standing invitees (every session invites them) the chair deliberately left off. */
  exclude_default_observers?: string[]
}

/** A People Leader the One-Up can seat — same shape as calibration's, so the
 *  RosterChecklist is shared. */
/** A One-Up HR can schedule on behalf of. */
export interface TalentChair {
  id: string
  name: string
  people_leaders: number
  reports_to_assess: number
  /** Sessions already scheduled for the year. */
  sessions: number
}

/** A seated People Leader's list as starting the session would find it. */
export interface RoomPlacementInput {
  employee_id: string
  performance: 'high' | 'medium' | 'low'
  potential: 'high' | 'medium' | 'low'
  rationale?: string
}

export interface RoomPlacementResult {
  employee_id: string
  manager_id: string
  performance: 'high' | 'medium' | 'low'
  potential: 'high' | 'medium' | 'low'
  rationale: string
}

export interface CancelSessionResult {
  session_id: string
  status: 'cancelled'
  /** True: cancelled for everyone invited. False: it could not be cancelled from here. Null: there was none. */
  meeting_cancelled: boolean | null
  /** Placements that were handed in and are drafts again. */
  returned_to_draft: number
}

export interface TalentStartSeat {
  user_id: string
  name: string
  /** The people they place today. */
  people: number
  /** How many of those are handed in. */
  submitted: number
  /** Their drafts place everyone and nothing is handed in: the chair can include the list. */
  ready: boolean
}

export interface AvailableTalentManager {
  id: string
  name: string
  /** How many of their reports they will have to place. */
  reports_to_assess: number
  assigned_session_id?: string | null
  assigned_session_date?: string | null
  /** Already submitted in that session — locked, cannot be moved. */
  submitted?: boolean
  assigned_session_status?: string | null
  /** May be seated in a new session: free, or their session started without their placements. */
  available?: boolean
  reason?: 'free' | 'scheduled' | 'submitted' | 'carry_over' | 'elsewhere'
  /** A lead one level below the chair's People Leaders (Sales, 30 Sep 2026): offered unticked;
   *  seated, they present their own team in the chair's session. */
  further_down?: boolean
  /** For a lead one level down: the People Leader they report to. */
  reports_to_name?: string | null
  /** reason 'elsewhere': who chairs the session where they already present these people. */
  assigned_chair_name?: string | null
}

export interface TalentReviewSessionUpdate {
  session_name?: string
  scheduled_date?: string
  /** A new length re-issues the invite, as a new date does. */
  duration_minutes?: number
  facilitator_id?: string
  facilitator_name?: string
  pre_review_deadline?: string
  notes?: string
  summary?: string
  agenda?: string[]
}

export interface TalentReviewSessionListResponse {
  sessions: TalentReviewSession[]
  total: number
  limit: number
  offset: number
  /** Whether this caller may ask for every session in the organisation. */
  can_view_all?: boolean
  /** What the server actually applied — 'mine' even if 'all' was asked for. */
  scope?: 'mine' | 'all'
}

// ============================================================================
// INTERFACES - TALENT REVIEW PARTICIPANTS
// ============================================================================

export interface TalentReviewParticipant {
  id: string
  session_id: string
  user_id: string
  role: TalentReviewParticipantRole
  user_name?: string
  user_title?: string
  invited_at: string
  invited_by?: string
  joined_at?: string
  left_at?: string
  pre_review_required: boolean
  pre_review_submitted: boolean
  pre_review_submitted_at?: string
  employees_to_assess: number
  employees_assessed: number
  created_at: string
  updated_at: string
}

export interface TalentReviewParticipantCreate {
  user_id: string
  role: TalentReviewParticipantRole
  user_name?: string
  user_title?: string
  invited_by?: string
  pre_review_required?: boolean
  employees_to_assess?: number
}

// ============================================================================
// INTERFACES - PRE-REVIEW SUBMISSIONS
// ============================================================================

export interface TalentReviewPreSubmission {
  id: string
  session_id: string
  manager_id: string
  employee_id: string
  manager_name?: string
  employee_name?: string
  employee_role?: string
  employee_department?: string
  proposed_performance: PerformanceLevel
  proposed_potential: PotentialLevel
  positioning_rationale?: string
  evidence_references?: string[]
  current_pipeline_level?: LeadershipPipelineLevel
  recommended_pipeline_level?: LeadershipPipelineLevel
  drotter_potential?: DrotterPotentialLevel
  turn_readiness?: string
  turn_readiness_rationale?: string
  nominated_as_key_person: boolean
  key_person_rationale?: string
  critical_knowledge_areas?: string[]
  retention_risk?: RetentionRisk
  nominated_for_succession: boolean
  succession_role_ids?: string[]
  succession_readiness?: SuccessorReadiness
  succession_rationale?: string
  development_priorities?: string[]
  recommended_interventions?: string[]
  status: PreSubmissionStatus
  submitted_at?: string
  reviewed_at?: string
  reviewed_by?: string
  flagged: boolean
  flag_reason?: string
  flagged_by?: string
  flagged_at?: string
  final_performance?: PerformanceLevel
  final_potential?: PotentialLevel
  position_changed: boolean
  change_rationale?: string
  created_at: string
  updated_at: string
}

export interface TalentReviewPreSubmissionCreate {
  manager_id: string
  employee_id: string
  manager_name?: string
  employee_name?: string
  employee_role?: string
  employee_department?: string
  proposed_performance: PerformanceLevel
  proposed_potential: PotentialLevel
  positioning_rationale?: string
  evidence_references?: string[]
  current_pipeline_level?: LeadershipPipelineLevel
  recommended_pipeline_level?: LeadershipPipelineLevel
  drotter_potential?: DrotterPotentialLevel
  turn_readiness?: string
  turn_readiness_rationale?: string
  nominated_as_key_person?: boolean
  key_person_rationale?: string
  critical_knowledge_areas?: string[]
  retention_risk?: RetentionRisk
  nominated_for_succession?: boolean
  succession_role_ids?: string[]
  succession_readiness?: SuccessorReadiness
  succession_rationale?: string
  development_priorities?: string[]
  recommended_interventions?: string[]
}

export interface TalentReviewPreSubmissionUpdate {
  proposed_performance?: PerformanceLevel
  proposed_potential?: PotentialLevel
  positioning_rationale?: string
  evidence_references?: string[]
  current_pipeline_level?: LeadershipPipelineLevel
  recommended_pipeline_level?: LeadershipPipelineLevel
  drotter_potential?: DrotterPotentialLevel
  turn_readiness?: string
  turn_readiness_rationale?: string
  nominated_as_key_person?: boolean
  key_person_rationale?: string
  critical_knowledge_areas?: string[]
  retention_risk?: RetentionRisk
  nominated_for_succession?: boolean
  succession_role_ids?: string[]
  succession_readiness?: SuccessorReadiness
  succession_rationale?: string
  development_priorities?: string[]
  recommended_interventions?: string[]
}

// ============================================================================
// INTERFACES - SESSION DECISIONS
// ============================================================================

export interface ActionItem {
  action: string
  assigned_to?: string
  assigned_to_name?: string
  due_date?: string
  status: 'pending' | 'in-progress' | 'completed' | 'overdue'
  notes?: string
}

export interface TalentReviewDecision {
  id: string
  session_id: string
  employee_id: string
  employee_name?: string
  employee_role?: string
  decision_type: DecisionType
  decision_summary?: string
  original_performance?: PerformanceLevel
  original_potential?: PotentialLevel
  final_performance?: PerformanceLevel
  final_potential?: PotentialLevel
  hipo_category?: string
  hipo_confirmed?: boolean
  key_person_designated?: boolean
  retention_risk?: RetentionRisk
  succession_role_id?: string
  succession_role_name?: string
  succession_readiness?: SuccessorReadiness
  /** A key person the room designated: why they are one, and the mitigating actions. */
  key_person_rationale?: string | null
  retention_mitigations?: string | null
  action_items?: ActionItem[]
  decided_by: string
  decided_by_name?: string
  decided_at: string
  follow_up_required: boolean
  follow_up_date?: string
  follow_up_status?: string
  /** An action or note the room sent to the growth plan. */
  to_growth_plan?: boolean
  /** What it becomes there: 'opportunity', an enablement kind, or 'focus'. */
  growth_plan_item?: GrowthPlanItemKind | null
  /** For a 'focus' item: the growth focus the room proposes. */
  growth_focus?: ReviewFocus | string | null
  created_at: string
  updated_at: string
}

export interface TalentReviewDecisionCreate {
  employee_id: string
  employee_name?: string
  employee_role?: string
  decision_type: DecisionType
  decision_summary?: string
  original_performance?: PerformanceLevel
  original_potential?: PotentialLevel
  final_performance?: PerformanceLevel
  final_potential?: PotentialLevel
  hipo_category?: string
  hipo_confirmed?: boolean
  key_person_designated?: boolean
  retention_risk?: RetentionRisk
  succession_role_id?: string
  succession_role_name?: string
  succession_readiness?: SuccessorReadiness
  /** For a key person the room designates: why they are one, and the mitigating actions, as a People Leader's nomination carries them. */
  key_person_rationale?: string
  retention_mitigations?: string
  action_items?: ActionItem[]
  /** The server fills it from the caller when omitted. */
  decided_by?: string
  decided_by_name?: string
  follow_up_required?: boolean
  follow_up_date?: string
  /** For an action or note (development / retention): put it on the growth plan at completion. */
  to_growth_plan?: boolean
  /** What it becomes there. Defaults to an on-the-job enablement. */
  growth_plan_item?: GrowthPlanItemKind
  /** For a 'focus' item: the growth focus the room proposes; a next position rides on succession_role_name / succession_readiness. */
  growth_focus?: ReviewFocus
}

// ============================================================================
// INTERFACES - SESSION ANALYTICS
// ============================================================================

export interface TalentReviewAnalytics {
  session_id: string
  review_year: number
  total_participants: number
  managers_participated: number
  employees_reviewed: number
  submissions_total: number
  submissions_on_time: number
  submissions_late: number
  flagged_for_discussion: number
  positions_unchanged: number
  positions_moved_up: number
  positions_moved_down: number
  positions_moved_lateral: number
  pre_distribution: Record<string, number>
  post_distribution: Record<string, number>
  hipos_proposed: number
  hipos_confirmed: number
  hipos_rejected: number
  new_hipos: number
  key_persons_proposed: number
  key_persons_designated: number
  high_retention_risks: number
  succession_updates: number
  critical_roles_covered: number
  critical_roles_at_risk: number
  total_action_items: number
  development_actions: number
  retention_actions: number
  succession_actions: number
  generated_at: string
}

// ============================================================================
// SERVICE
// ============================================================================

// ============================================================================
// INTERFACES - RESETTING THE REVIEW ON A TEST ENVIRONMENT
// ============================================================================

/** A session a reset removes. */
export interface TalentReviewResetSession {
  id: string
  name: string | null
  status: string
  scheduled_date: string | null
  calendar_invite: boolean
  chair_id: string | null
  chair_name: string | null
  review_year: number | null
  leaders: number
  placements: number
  decisions: number
}

/** What resetting the Annual I&G Review would remove (GET /performance/talent/demo-reset). */
export interface TalentReviewResetPreview {
  review_year: number
  chair_id: string | null
  chairs: Array<{ id: string; name: string | null; sessions: number }>
  sessions: TalentReviewResetSession[]
  published: {
    placements: number
    high_potentials: number
    key_persons: number
    succession_plans: number
    growth_plan_items: number
  }
  baseline_rows: number
}

/** What a reset removed, where the copy went, and what was put back. */
export interface TalentReviewResetResult extends TalentReviewResetPreview {
  backup_prefix: string | null
  starting_point: { restored_from: 'baseline' | 'dev_seeds' | null; rows: number }
}

class TalentReviewService {
  private baseURL = API_CONFIG.baseURL

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers as Record<string, string>),
      },
    })

    if (!response.ok) {
      // Carry the server's status and detail. This used to throw the literal
      // string "HTTP error! status: 409 - {...json...}", which is what a People
      // Leader would have read on screen. Every refusal the session gates
      // produce is prose meant for them — fourth service to get this fix.
      const raw = await response.text()
      let detail = raw
      try {
        const parsed = JSON.parse(raw)
        detail = typeof parsed?.detail === 'string'
          ? parsed.detail
          : Array.isArray(parsed?.detail)
            ? parsed.detail.map((d: { msg?: string }) => d?.msg).filter(Boolean).join('; ') || raw
            : raw
      } catch {
        /* not JSON — keep the raw body */
      }
      const err = new Error(detail || `Request failed (${response.status})`) as TalentReviewApiError
      err.status = response.status
      err.detail = detail
      throw err
    }

    if (response.status === 204) {
      return null as T
    }

    return response.json()
  }

  // ============================================================================
  // TALENT PROFILES
  // ============================================================================

  /**
   * Get talent profile for a specific user
   */
  async getProfile(userId: string): Promise<TalentProfile> {
    return this.request<TalentProfile>(`/performance/talent/profiles/${userId}`)
  }

  /**
   * Generate or refresh a talent profile from performance data
   */
  async generateProfile(userId: string): Promise<{ message: string; profile_id: string }> {
    return this.request(`/performance/talent/profiles/${userId}/generate`, {
      method: 'POST',
    })
  }

  // ============================================================================
  // 9-BOX GRID
  // ============================================================================

  /**
   * Get the 9-box grid for a specific review year
   */
  async getNineBoxGrid(
    reviewYear: number,
    orgUnitId?: string
  ): Promise<NineBoxGrid> {
    const params = new URLSearchParams()
    params.set('review_year', reviewYear.toString())
    if (orgUnitId) params.set('org_unit_id', orgUnitId)
    
    return this.request<NineBoxGrid>(`/performance/talent/9box?${params}`)
  }

  /**
   * Position an employee on the 9-box grid
   */
  async createNineBoxPosition(data: NineBoxPositionCreate): Promise<NineBoxPosition> {
    return this.request<NineBoxPosition>('/performance/talent/9box/positions', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update an employee's 9-box position
   */
  async updateNineBoxPosition(
    positionId: string,
    updates: {
      performance_level?: PerformanceLevel
      potential_level?: PotentialLevel
      rationale?: string
    }
  ): Promise<{ message: string; position_id: string }> {
    const params = new URLSearchParams()
    if (updates.performance_level) params.set('performance_level', updates.performance_level)
    if (updates.potential_level) params.set('potential_level', updates.potential_level)
    if (updates.rationale) params.set('rationale', updates.rationale)
    
    return this.request(`/performance/talent/9box/positions/${positionId}?${params}`, {
      method: 'PATCH',
    })
  }

  // ============================================================================
  // SUCCESSION PLANNING
  // ============================================================================

  /**
   * List all succession plans
   */
  async listSuccessionPlans(params?: {
    review_year?: number
    org_unit_id?: string
    risk_level?: string
  }): Promise<SuccessionPlan[]> {
    const searchParams = new URLSearchParams()
    if (params?.review_year) searchParams.set('review_year', params.review_year.toString())
    if (params?.org_unit_id) searchParams.set('org_unit_id', params.org_unit_id)
    if (params?.risk_level) searchParams.set('risk_level', params.risk_level)
    
    const query = searchParams.toString()
    return this.request<SuccessionPlan[]>(
      `/performance/talent/succession-plans${query ? `?${query}` : ''}`
    )
  }

  /**
   * Create a succession plan for a critical role
   */
  async createSuccessionPlan(data: SuccessionPlanCreate): Promise<SuccessionPlan> {
    return this.request<SuccessionPlan>('/performance/talent/succession-plans', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Add a candidate to a succession plan
   */
  async addSuccessionCandidate(
    planId: string,
    data: SuccessionCandidateCreate
  ): Promise<{ message: string; candidate_id: string }> {
    return this.request(`/performance/talent/succession-plans/${planId}/candidates`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // ============================================================================
  // KEY PERSONS
  // ============================================================================

  /**
   * List all key person designations
   */
  async listKeyPersons(params?: {
    review_year?: number
    retention_risk?: RetentionRisk
  }): Promise<KeyPerson[]> {
    const searchParams = new URLSearchParams()
    if (params?.review_year) searchParams.set('review_year', params.review_year.toString())
    if (params?.retention_risk) searchParams.set('retention_risk', params.retention_risk)
    
    const query = searchParams.toString()
    return this.request<KeyPerson[]>(
      `/performance/talent/key-persons${query ? `?${query}` : ''}`
    )
  }

  /**
   * Designate an employee as a key person
   */
  async designateKeyPerson(data: KeyPersonCreate): Promise<KeyPerson> {
    return this.request<KeyPerson>('/performance/talent/key-persons', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // ============================================================================
  // HIPO TRACKING
  // ============================================================================

  /**
   * List all HIPO designations
   * HIPOs are auto-identified from top 3 blocks of 9-box grid:
   * - Stars: High Performance / High Potential
   * - High Professionals: High Performance / Medium Potential
   * - High Potentials: Medium Performance / High Potential
   */
  async listHIPOs(params?: {
    review_year?: number
    category?: HIPOCategory
  }): Promise<HIPODesignation[]> {
    const searchParams = new URLSearchParams()
    if (params?.review_year) searchParams.set('review_year', params.review_year.toString())
    if (params?.category) searchParams.set('category', params.category)
    
    const query = searchParams.toString()
    return this.request<HIPODesignation[]>(
      `/performance/talent/hipos${query ? `?${query}` : ''}`
    )
  }

  // ============================================================================
  // BENCH STRENGTH
  // ============================================================================

  /**
   * Get bench strength analysis for a review year
   */
  async getBenchStrength(
    reviewYear: number,
    analysisLevel?: string
  ): Promise<BenchStrengthAnalysis[]> {
    const params = new URLSearchParams()
    params.set('review_year', reviewYear.toString())
    if (analysisLevel) params.set('analysis_level', analysisLevel)
    
    return this.request<BenchStrengthAnalysis[]>(
      `/performance/talent/bench-strength?${params}`
    )
  }

  // ============================================================================
  // TALENT REVIEW SESSIONS (Enhanced with phase workflow)
  // ============================================================================

  /**
   * List talent review sessions with pagination
   */
  async listSessions(params?: {
    review_year?: number
    status?: TalentReviewSessionStatus
    org_unit_id?: string
    /** 'mine' (the default) — what the caller chairs or is in; 'all' — every
     *  session, honoured only for a caller entitled to it. The scope is the
     *  server's decision: it reads the token, not this parameter. */
    scope?: 'mine' | 'all'
    /** Match the session name, its chair, or a seated People Leader. */
    q?: string
    limit?: number
    offset?: number
  }): Promise<TalentReviewSessionListResponse> {
    const searchParams = new URLSearchParams()
    if (params?.review_year) searchParams.set('review_year', params.review_year.toString())
    if (params?.status) searchParams.set('status', params.status)
    if (params?.org_unit_id) searchParams.set('org_unit_id', params.org_unit_id)
    if (params?.scope) searchParams.set('scope', params.scope)
    if (params?.q) searchParams.set('q', params.q)
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.offset) searchParams.set('offset', params.offset.toString())
    
    const query = searchParams.toString()
    return this.request<TalentReviewSessionListResponse>(
      `/performance/talent/sessions${query ? `?${query}` : ''}`
    )
  }

  /**
   * Get a specific talent review session
   */
  async getSession(sessionId: string): Promise<TalentReviewSession> {
    return this.request<TalentReviewSession>(`/performance/talent/sessions/${sessionId}`)
  }

  /**
   * Create a new talent review session
   */
  async createSession(data: TalentReviewSessionCreate): Promise<TalentReviewSession> {
    return this.request<TalentReviewSession>('/performance/talent/sessions', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /** People Leaders a One-Up can seat when scheduling, with where each sits now. */
  async getAvailableManagers(
    facilitatorId: string,
    reviewYear: number,
    /** Leave one session out: for "who may be in THIS session", being in it is not a reason against. */
    excludeSessionId?: string,
  ): Promise<{ managers: AvailableTalentManager[] }> {
    const params = new URLSearchParams({ facilitator_id: facilitatorId, review_year: String(reviewYear) })
    if (excludeSessionId) params.set('exclude_session_id', excludeSessionId)
    return this.request(`/performance/talent/sessions/available-managers?${params}`)
  }

  /**
   * A People Leader's reviewable direct reports for a session, with the
   * performance level pre-filled from the mid-year rating and whatever has
   * already been submitted. manager_id defaults to the caller server-side.
   */
  async getPlacementRoster(sessionId: string, managerId?: string): Promise<PlacementRoster> {
    const q = managerId ? `?manager_id=${encodeURIComponent(managerId)}` : ''
    return this.request<PlacementRoster>(`/performance/talent/sessions/${sessionId}/placement-roster${q}`)
  }

  /** The year's published high-potentials with their plans and the forum's last review (HR). */
  async getHipoWorklist(reviewYear: number): Promise<HipoWorklist> {
    return this.request<HipoWorklist>(`/performance/talent/hipo-worklist?review_year=${reviewYear}`)
  }

  /** The room amends a high-potential’s growth details while the session is live. */
  async amendGrowthDetails(sessionId: string, employeeId: string, body: GrowthDetailsAmendment): Promise<PlacementSubmissionState> {
    return this.request<PlacementSubmissionState>(`/performance/talent/sessions/${sessionId}/placements/${employeeId}/growth`, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  }

  /** The talent map as a PDF — the nine blocks with every name in them.
   *  Server-rendered, like the session report: no pop-up, no print dialog. */
  async downloadGridPdf(params: {
    reviewYear: number
    sessionId?: string
    managerId?: string
    title?: string
    function?: string
    department?: string
    campus?: string
    position_type?: string
    leader_id?: string
    one_up_id?: string
  }): Promise<void> {
    const q = new URLSearchParams({ review_year: String(params.reviewYear) })
    if (params.sessionId) q.set('session_id', params.sessionId)
    if (params.managerId) q.set('manager_id', params.managerId)
    if (params.title) q.set('title', params.title)
    for (const key of ['function', 'department', 'campus', 'position_type', 'leader_id', 'one_up_id'] as const) {
      const value = params[key]
      if (value) q.set(key, value)
    }
    const response = await authenticatedFetch(`${this.baseURL}/performance/talent/grid.pdf?${q.toString()}`)
    if (!response.ok) {
      throw new Error(`Could not build the PDF: ${response.status} ${await response.text()}`)
    }
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const disposition = response.headers.get('Content-Disposition')
    a.download = disposition?.match(/filename="?(.+?)"?$/)?.[1] || `Eduvos-Talent-Map-${params.reviewYear}.pdf`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  /** The campuses this caller may open the input space for. */
  async listCampuses(reviewYear: number): Promise<CampusListResponse> {
    return this.request<CampusListResponse>(`/performance/talent/campuses?review_year=${reviewYear}`)
  }

  /** Everyone at a campus, whatever their reporting line, with the input given on them. */
  async getCampusView(reviewYear: number, campus: string): Promise<CampusViewResponse> {
    return this.request<CampusViewResponse>(
      `/performance/talent/campus-view?review_year=${reviewYear}&campus=${encodeURIComponent(campus)}`,
    )
  }

  /** Record — or clear — this caller's input on one person. */
  async saveCampusInput(employeeId: string, body: CampusInputWrite): Promise<{ employee_id: string; input: CampusInput | null }> {
    return this.request(`/performance/talent/campus-input/${encodeURIComponent(employeeId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  }

  /** The platform directory: anyone active, by name, email, title or personnel number. */
  async searchPeople(q: string, limit = 20): Promise<{ people: DirectoryPerson[] }> {
    return this.request<{ people: DirectoryPerson[] }>(`/performance/talent/people?q=${encodeURIComponent(q)}&limit=${limit}`)
  }

  /** The room adds a successor for a seated leader's role (or the chair's) during the session. */
  async addRoomSuccessor(sessionId: string, body: RoomSuccessorInput): Promise<SuccessorNomination> {
    return this.request<SuccessorNomination>(`/performance/talent/sessions/${sessionId}/successors/room`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /** HR's standing invitees — the people every Annual I&G Review invites by default. */
  async listDefaultObservers(): Promise<DefaultObserver[]> {
    return this.request<DefaultObserver[]>('/performance/talent/default-observers')
  }

  /** The One-Ups HR can schedule a session for (HR only). */
  async listChairs(reviewYear: number): Promise<{ chairs: TalentChair[] }> {
    return this.request<{ chairs: TalentChair[] }>(`/performance/talent/sessions/chairs?review_year=${reviewYear}`)
  }

  /** Stamp a HIPO's plan as reviewed by the forum today, with a note. */
  async recordForumReview(
    userId: string,
    reviewYear: number,
    note?: string,
  ): Promise<{ employee_id: string; forum_reviewed_at: string; forum_reviewed_by: string | null; forum_review_note: string | null }> {
    return this.request(`/performance/talent/hipos/${encodeURIComponent(userId)}/forum-review?review_year=${reviewYear}`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    })
  }

  /** Undo a decision taken in the room; the live grid and publish read the rest in order. */
  async undoDecision(sessionId: string, decisionId: string): Promise<{ message: string; id: string }> {
    return this.request<{ message: string; id: string }>(`/performance/talent/sessions/${sessionId}/decisions/${decisionId}`, {
      method: 'DELETE',
    })
  }

  /** Tick or untick a step of the room's running order. */
  async setAgendaStep(
    sessionId: string,
    step: AgendaStepId,
    done: boolean,
  ): Promise<{ session_id: string; agenda_progress: Record<string, string> }> {
    return this.request(`/performance/talent/sessions/${sessionId}/agenda`, {
      method: 'PUT',
      body: JSON.stringify({ step, done }),
    })
  }

  /** Reopen a completed session, with a reason for the record. */
  async reopenSession(sessionId: string, reason: string): Promise<{ message: string; status: TalentReviewSessionStatus }> {
    return this.request(`/performance/talent/sessions/${sessionId}/reopen`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  }

  /** One People Leader's successor nominations (manager_id), or the whole session's (chair / HR). */
  async getSuccessorNominations(sessionId: string, managerId?: string): Promise<SuccessorNominationList> {
    const q = managerId ? `?manager_id=${encodeURIComponent(managerId)}` : ''
    return this.request<SuccessorNominationList>(`/performance/talent/sessions/${sessionId}/successors${q}`)
  }

  /** Replace a People Leader's successor nominations as a set. */
  async saveSuccessorNominations(sessionId: string, body: SuccessorNominationBatchInput): Promise<SuccessorNominationList> {
    return this.request<SuccessorNominationList>(`/performance/talent/sessions/${sessionId}/successors`, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  }

  /** The room's word on a nomination. */
  async decideSuccessor(
    sessionId: string,
    nominationId: string,
    body: { status: SuccessorStatus; note?: string; readiness?: SuccessorReadiness },
  ): Promise<SuccessorNomination> {
    return this.request<SuccessorNomination>(`/performance/talent/sessions/${sessionId}/successors/${nominationId}/decision`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /** Record (or re-record) a People Leader's placements in one go. */
  async submitPlacements(sessionId: string, body: PlacementBatchInput): Promise<PlacementRoster> {
    return this.request<PlacementRoster>(`/performance/talent/sessions/${sessionId}/placements`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * The talent map: everyone in scope with their block, enrichment and
   * development plan. With session_id it reads that session's placements
   * and decisions; without it, the published positions for the year. The
   * server decides the caller's scope (HR org-wide, a People Leader two
   * levels down). Undefined filters are left out of the query string.
   */
  async getTalentGrid(params: TalentGridParams): Promise<TalentGridResponse> {
    const q = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '') continue
      q.set(key, String(value))
    }
    return this.request<TalentGridResponse>(`/performance/talent/grid?${q}`)
  }

  /**
   * Update a talent review session
   */
  async updateSession(sessionId: string, data: TalentReviewSessionUpdate): Promise<TalentReviewSession> {
    return this.request<TalentReviewSession>(`/performance/talent/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete/cancel a talent review session
   */
  async deleteSession(sessionId: string, force?: boolean): Promise<{ message: string; session_id: string }> {
    const params = force ? '?force=true' : ''
    return this.request(`/performance/talent/sessions/${sessionId}${params}`, {
      method: 'DELETE',
    })
  }

  /** The chair, or HR, cancels a session that has not started: its Outlook meeting is cancelled
   *  for everyone invited, and placements already handed in go back to drafts so they follow
   *  their People Leader to the next session (Victor, 7 Oct 2026). */
  async cancelSession(sessionId: string, reason?: string): Promise<CancelSessionResult> {
    return this.request<CancelSessionResult>(`/performance/talent/sessions/${sessionId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || null }),
    })
  }

  /**
   * What resetting the Annual I&G Review would remove: one chair's sessions, or every chair's.
   * Test environments only, for admin and HR admin.
   */
  async getDemoResetPreview(facilitatorId?: string): Promise<TalentReviewResetPreview> {
    const params = facilitatorId ? `?facilitator_id=${encodeURIComponent(facilitatorId)}` : ''
    return this.request(`/performance/talent/demo-reset${params}`)
  }

  /**
   * Reset the Annual I&G Review to before any session was scheduled.
   * Test environments only, for admin and HR admin.
   */
  async resetDemo(facilitatorId?: string): Promise<TalentReviewResetResult> {
    const params = facilitatorId ? `?facilitator_id=${encodeURIComponent(facilitatorId)}` : ''
    return this.request(`/performance/talent/demo-reset${params}`, { method: 'POST' })
  }

  // ============================================================================
  // SESSION PHASE TRANSITIONS
  // ============================================================================

  /**
   * Begin pre-review phase (SCHEDULED → PRE_REVIEW)
   */
  async beginPreReview(sessionId: string): Promise<{ message: string; session_id: string; status: string }> {
    return this.request(`/performance/talent/sessions/${sessionId}/begin-pre-review`, {
      method: 'POST',
    })
  }

  /**
   * Generate talent report (PRE_REVIEW → REPORT_READY)
   */
  async generateReport(sessionId: string): Promise<{ message: string; session_id: string; status: string; report_summary: Record<string, number> }> {
    return this.request(`/performance/talent/sessions/${sessionId}/generate-report`, {
      method: 'POST',
    })
  }

  /**
   * Start live session (REPORT_READY → IN_SESSION)
   */
  /** What starting would find in each People Leader's list: how many are handed in, and
   *  whether their drafts place everyone ("ready") so the chair can include them. */
  async getStartCheck(sessionId: string): Promise<{ seats: TalentStartSeat[] }> {
    return this.request(`/performance/talent/sessions/${sessionId}/start-check`)
  }

  /** Open the room. `releaseUnsubmitted` takes out the People Leaders who have
   *  submitted nothing, so they can be placed in a later session — the lists
   *  lock here, so their seat could never be filled in this one.
   *  `includeDraftsOf` first hands in, as they stand, the lists of People Leaders
   *  whose drafts place everyone (Victor, 5 Oct 2026). */
  async startSession(
    sessionId: string,
    releaseUnsubmitted = false,
    includeDraftsOf: string[] = [],
  ): Promise<{
    message: string
    session_id: string
    status: string
    released?: Array<{ user_id: string; name: string; people: number }>
    included?: Array<{ user_id: string; name: string; people: number }>
  }> {
    const params = new URLSearchParams()
    if (releaseUnsubmitted) params.set('release_unsubmitted', 'true')
    includeDraftsOf.forEach((id) => params.append('include_drafts_of', id))
    const q = params.toString() ? `?${params.toString()}` : ''
    return this.request(`/performance/talent/sessions/${sessionId}/start${q}`, {
      method: 'POST',
    })
  }

  /**
   * End live session (IN_SESSION → POST_REVIEW)
   */
  async endSession(sessionId: string): Promise<{ message: string; session_id: string; status: string }> {
    return this.request(`/performance/talent/sessions/${sessionId}/end-session`, {
      method: 'POST',
    })
  }

  /**
   * Complete session (POST_REVIEW → COMPLETED)
   */
  async completeSession(sessionId: string, summary?: string): Promise<{ message: string; session_id: string; status: string; metrics: Record<string, number> }> {
    const params = summary ? `?summary=${encodeURIComponent(summary)}` : ''
    return this.request(`/performance/talent/sessions/${sessionId}/complete${params}`, {
      method: 'POST',
    })
  }

  // ============================================================================
  // SESSION PARTICIPANTS
  // ============================================================================

  /**
   * List participants for a session
   */
  async listParticipants(sessionId: string): Promise<TalentReviewParticipant[]> {
    return this.request<TalentReviewParticipant[]>(
      `/performance/talent/sessions/${sessionId}/participants`
    )
  }

  /**
   * Add a participant to a session
   */
  async addParticipant(sessionId: string, data: TalentReviewParticipantCreate): Promise<TalentReviewParticipant> {
    return this.request<TalentReviewParticipant>(
      `/performance/talent/sessions/${sessionId}/participants`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  }

  /**
   * Remove a participant from a session
   */
  /** Who is in the session, after it was scheduled. Each list is the whole
   *  list for its kind; leave one out to change only the other. The server
   *  re-issues the calendar invite. */
  async setAttendees(
    sessionId: string,
    body: { managers?: string[]; observers?: string[] },
  ): Promise<TalentReviewParticipant[]> {
    return this.request<TalentReviewParticipant[]>(`/performance/talent/sessions/${sessionId}/attendees`, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  }

  async removeParticipant(sessionId: string, participantId: string): Promise<{ message: string; participant_id: string }> {
    return this.request(
      `/performance/talent/sessions/${sessionId}/participants/${participantId}`,
      {
        method: 'DELETE',
      }
    )
  }

  // ============================================================================
  // PRE-REVIEW SUBMISSIONS
  // ============================================================================

  /**
   * List pre-review submissions for a session
   */
  async listPreSubmissions(sessionId: string, params?: {
    manager_id?: string
    status?: PreSubmissionStatus
  }): Promise<TalentReviewPreSubmission[]> {
    const searchParams = new URLSearchParams()
    if (params?.manager_id) searchParams.set('manager_id', params.manager_id)
    if (params?.status) searchParams.set('status', params.status)
    
    const query = searchParams.toString()
    return this.request<TalentReviewPreSubmission[]>(
      `/performance/talent/sessions/${sessionId}/submissions${query ? `?${query}` : ''}`
    )
  }

  /**
   * Create a pre-review submission
   */
  async createPreSubmission(sessionId: string, data: TalentReviewPreSubmissionCreate): Promise<TalentReviewPreSubmission> {
    return this.request<TalentReviewPreSubmission>(
      `/performance/talent/sessions/${sessionId}/submissions`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  }

  /**
   * Update a pre-review submission
   */
  async updatePreSubmission(submissionId: string, data: TalentReviewPreSubmissionUpdate): Promise<TalentReviewPreSubmission> {
    return this.request<TalentReviewPreSubmission>(
      `/performance/talent/submissions/${submissionId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    )
  }

  /**
   * Submit a pre-review submission for review
   */
  async submitPreSubmission(submissionId: string): Promise<{ message: string; submission_id: string }> {
    return this.request(`/performance/talent/submissions/${submissionId}/submit`, {
      method: 'POST',
    })
  }

  /**
   * Withdraw a submitted pre-review submission back to draft
   */
  async withdrawPreSubmission(submissionId: string): Promise<{ message: string; submission_id: string }> {
    return this.request(`/performance/talent/submissions/${submissionId}/withdraw`, {
      method: 'POST',
    })
  }

  /**
   * Flag a submission for discussion
   */
  async flagPreSubmission(submissionId: string, flagReason: string, flaggedBy: string): Promise<{ message: string; submission_id: string }> {
    const params = new URLSearchParams({
      flag_reason: flagReason,
      flagged_by: flaggedBy,
    })
    return this.request(`/performance/talent/submissions/${submissionId}/flag?${params}`, {
      method: 'POST',
    })
  }

  // ============================================================================
  // SESSION DECISIONS
  // ============================================================================

  /**
   * List decisions for a session
   */
  async listDecisions(sessionId: string, decisionType?: DecisionType): Promise<TalentReviewDecision[]> {
    const params = decisionType ? `?decision_type=${decisionType}` : ''
    return this.request<TalentReviewDecision[]>(
      `/performance/talent/sessions/${sessionId}/decisions${params}`
    )
  }

  /**
   * Create a decision
   */
  async createDecision(sessionId: string, data: TalentReviewDecisionCreate): Promise<TalentReviewDecision> {
    return this.request<TalentReviewDecision>(
      `/performance/talent/sessions/${sessionId}/decisions`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  }

  /** The room places someone who has no handed-in placement: they joined their People
   *  Leader's list after it was handed in, or after the session started (Victor, 6 Oct 2026).
   *  The chair, or HR. It stands as their People Leader's placement in this session. */
  async placeInRoom(sessionId: string, data: RoomPlacementInput): Promise<RoomPlacementResult> {
    return this.request<RoomPlacementResult>(`/performance/talent/sessions/${sessionId}/room-placements`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // ============================================================================
  // SESSION ANALYTICS
  // ============================================================================

  /**
   * Get session analytics
   */
  async getSessionAnalytics(sessionId: string): Promise<TalentReviewAnalytics> {
    return this.request<TalentReviewAnalytics>(
      `/performance/talent/sessions/${sessionId}/analytics`
    )
  }

  // ============================================================================
  // REPORT DOWNLOAD
  // ============================================================================

  /**
   * Download a professional PDF report for a talent review session.
   * Triggers a browser file download.
   */
  async downloadReport(sessionId: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.baseURL}/performance/talent/sessions/${sessionId}/download-report`
    )

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to download report: ${response.status} - ${errorText}`)
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url

    // Extract filename from Content-Disposition header or use default
    const disposition = response.headers.get('Content-Disposition')
    const filenameMatch = disposition?.match(/filename="?(.+?)"?$/)
    a.download = filenameMatch?.[1] || `Eduvos_Talent_Review_Report_${sessionId}.pdf`

    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Get 9-box grid box label for display
   */
  getBoxLabel(performance: PerformanceLevel, potential: PotentialLevel): string {
    const labels: Record<string, string> = {
      'high_high': 'Star',
      'high_medium': 'High Professional',
      'high_low': 'Workhorse',
      'medium_high': 'High Potential',
      'medium_medium': 'Core Player',
      'medium_low': 'Inconsistent Performer',
      'low_high': 'Enigma',
      'low_medium': 'Dilemma',
      'low_low': 'Underperformer',
    }
    return labels[`${performance}_${potential}`] || 'Unknown'
  }

  /**
   * Check if position qualifies as HIPO
   */
  isHIPOPosition(performance: PerformanceLevel, potential: PotentialLevel): boolean {
    return (
      (performance === 'high' && (potential === 'high' || potential === 'medium')) ||
      (performance === 'medium' && potential === 'high')
    )
  }

  /**
   * Get HIPO category based on 9-box position
   */
  getHIPOCategory(performance: PerformanceLevel, potential: PotentialLevel): HIPOCategory | null {
    if (performance === 'high' && potential === 'high') return 'star'
    if (performance === 'high' && potential === 'medium') return 'high-professional'
    if (performance === 'medium' && potential === 'high') return 'high-potential'
    return null
  }

  /**
   * Get retention risk color
   */
  getRetentionRiskColor(risk?: RetentionRisk): string {
    const colors: Record<RetentionRisk, string> = {
      low: 'text-green-600',
      medium: 'text-amber-600',
      high: 'text-orange-600',
      critical: 'text-red-600',
    }
    return risk ? colors[risk] : 'text-gray-400'
  }

  /**
   * Get succession readiness color
   */
  getReadinessColor(readiness: SuccessorReadiness): string {
    const colors: Record<SuccessorReadiness, string> = {
      'ready-now': 'text-green-600 bg-green-50',
      'ready-1-year': 'text-amber-600 bg-amber-50',
      'ready-2-plus': 'text-blue-600 bg-blue-50',
    }
    return colors[readiness]
  }
}

export const talentReviewService = new TalentReviewService()
export default talentReviewService
